/**
 * 从 OSS AppConfig JSON 拉取域名，写入 window.$config.AppConfig
 */

import { syncAxiosBaseURL } from "@/utils/request";
import { getCurrentDomainConfig } from "@/utils/hybirdMethods";
import { getBrowser } from "@/utils/browser";
import {
  resolveHosts,
  getSelectedChannel,
  setSelectedChannel,
  ensureFallbackAppConfig,
  applyAppDomainConfigFromBridge,
  applyAppHostsFromUa,
  resetApiDomainConfigLog,
  resetLastSuccessApiHostIfInvalid,
  getPreferredApiHost
} from "@/utils/apiHost";

export { setSelectedChannel, getSelectedChannel };

function afterAppConfigReady() {
  resetLastSuccessApiHostIfInvalid();
  const cfg = (window as any).$config;
  if (cfg && getPreferredApiHost()) {
    cfg.VUE_APP_API_HOST = getPreferredApiHost();
  }
  syncAxiosBaseURL();
}

export function applyAppConfig(data: Record<string, any>) {
  const cfg = (window as any).$config;
  if (!cfg || !data) return;

  const channel = getSelectedChannel();

  if (data.AppConfig) {
    cfg.AppConfig = {
      api: data.AppConfig.api || [],
      ws: data.AppConfig.ws || []
    };
    const apiHosts = resolveHosts(cfg.AppConfig.api, channel);
    if (apiHosts[0]) cfg.VUE_APP_API_HOST = apiHosts[0];
    const wsHosts = resolveHosts(cfg.AppConfig.ws, channel);
    if (wsHosts[0]) cfg.VUE_APP_WS_HOST = wsHosts[0];
  }

  const h5 = data.h5;
  if (h5) {
    const h5Host = resolveHosts(h5.H5Urls || h5.h5, channel)[0];
    if (h5Host) cfg.VUE_APP_GET_URL = `${h5Host}/personal`;
    const idcHost = resolveHosts(h5.idcUrl, channel)[0];
    if (idcHost) cfg.VUE_APP_IDC_ACTIVITY_URL = idcHost;
  }
}

function loadAppConfigFromApp(): Promise<void> {
  console.log("[域名配置] App 环境，开始 getCurrentDomainConfig");
  return getCurrentDomainConfig()
    .then(data => {
      if (data && applyAppDomainConfigFromBridge(data)) {
        if (data.AppConfig || data.h5) {
          applyAppConfig(data);
        }
        ensureFallbackAppConfig();
        afterAppConfigReady();
        return;
      }
      if (applyAppHostsFromUa()) {
        ensureFallbackAppConfig();
        afterAppConfigReady();
        return;
      }
      ensureFallbackAppConfig();
      afterAppConfigReady();
    })
    .catch(() => {
      if (applyAppHostsFromUa()) {
        ensureFallbackAppConfig();
        afterAppConfigReady();
        return;
      }
      ensureFallbackAppConfig();
      afterAppConfigReady();
    });
}

export function loadAppConfig(forceRefresh = false): Promise<void> {
  if (forceRefresh) resetApiDomainConfigLog();

  if (getBrowser().isApp) {
    return loadAppConfigFromApp();
  }

  const cfg = (window as any).$config || {};
  const url = cfg.VUE_APP_APP_CONFIG_URL;
  if (!url) {
    ensureFallbackAppConfig();
    afterAppConfigReady();
    return Promise.resolve();
  }

  const fetchUrl = forceRefresh
    ? `${url}${url.indexOf("?") > -1 ? "&" : "?"}t=${Date.now()}`
    : url;

  return fetch(fetchUrl, { cache: forceRefresh ? "no-store" : "default" })
    .then(res => {
      if (!res.ok) throw new Error(`appConfig http ${res.status}`);
      return res.json();
    })
    .then(data => {
      applyAppConfig(data);
      afterAppConfigReady();
    })
    .catch(() => {
      ensureFallbackAppConfig();
      afterAppConfigReady();
    });
}

export function reloadAppConfig(): Promise<void> {
  return loadAppConfig(true);
}
